import React, { useContext } from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import _ from 'lodash';

import Dropdown from '@src/components/common/dropdown';
import { ArrowDownIcon, SearchIcon, FilterV2Icon } from '@src/components/common/icon';
import { ICategory } from '@src/types/category';
import CardList from '@src/components/category/cardList';
import Pagination from '@src/components/common/paginationV2';
import { ScrollToTopContext, ScrollToTopContextType } from '@src/components/scrollToTop';

import './global.module.less';
import Empty from './empty';

const Content = styled.div`
	padding: 16px 0 16px 32px;
	width: 100%;
	height: 100%;
	@media 
		${props => props.theme.device.mobile},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet}
		{
			padding: 16px 0;
		}
`;

const Header = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;
`;

const TitleContent = styled.h1`
	font-size: 22px;
	color: #101010;
	font-weight: 400;
	margin-bottom: 0px;
`;

const ActionContent = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;

	@media 
		${props => props.theme.device.mobile},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet}
		{
			display: none;
		}
`;

const ActionContentMobile = styled.div`
	display: none;
	cursor: pointer;
	@media 
		${props => props.theme.device.mobile},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet}
		{
			display: flex;
			justify-content: center;
		}
`;

const StyledFilterV2Icon = styled(FilterV2Icon)`
	padding-bottom: 3px;
`;

const ActionText = styled.div`
	font-size: 16px;
	color: #676767 !important;
	margin-left: 3px;
`;

const SortContent = styled.div`
	font-size: 16px;
	color: ${props => (props.isExpand ? props.theme.colors.primary : '#676767')};
	display: flex;
	flex-direction: row;
	align-items: center;
	& .arrow-icon {
		path {
			fill: ${props => (props.isExpand ? props.theme.colors.primary : '#676767')};
		}
	}
`;

const StyledArrowDownIcon = styled(ArrowDownIcon)`
	margin: 0 24px 0 6px;
`;

const DescContent = styled.div`
	padding: 20px;
	font-size: 16px;
	color: #333333;
	border: 1px solid #E9E9E9;
	border-radius: 4px;

	@media ${props => props.theme.device.mobile} {
		padding: 16px;
		font-size: 14px;
	}
`;

const StyledInputBox = styled.input`
	border: none !important;
	padding: 0px 4px;
	font-size: 16px;
	width: 100%;
	color: #101010;

	&::placeholder {
		color: #9C9C9C;
		font-weight: 300;
		font-size: 16px;
	}
`;

const SearchInputWrapper = styled.div`
	display: none;
	background: #FFFFFF;
	border: 1px solid #DDDDDD;
	border-radius: 4px;
	display: flex;
	align-items: center;
	padding: 0px 8px;
	width: 100%;
	height: 38px;
	:focus-within {
		border: 1px solid #101010;
	}
	:hover {
		border: 1px solid #101010;
	}

  @media
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet}
		{
			width: 50%;
		}

	@media ${props => props.theme.device.desktop} {
		width: 170px;
	}
	@media ${props => props.theme.device.lgDesktop} {
		width: 185px;
	}
`;

const StyledSearchIcon = styled(SearchIcon)`
	fill: #B9B9B9;
`;

export const FilterGroup = styled.div`
	display: flex;
	justify-content: flex-end;

	@media
		${props => props.theme.device.tablet},
		${props => props.theme.device.smTablet}
		{
			display: grid;
			grid-template-columns: repeat(${(props: { columns: number }) => props.columns}, 1fr);
			grid-column-gap: 12px;
		}

	@media ${props => props.theme.device.mobile} {
		flex-direction: column;
	}
`;

export const StyledDropdown = styled(Dropdown)`
	font-size: 16px;
	cursor: pointer;
	& .options {
		width: 180px !important;
		right: 0px;
		border: 0px;
		box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.1);
		border-radius: 4px;
	}
	:hover {
		.sort-content {
			color: ${props => props.theme.colors.primary}
		}
		.arrow-icon {
			path {
				fill: ${props => props.theme.colors.primary}
			}
		}
	}
`;

export const PaginationBox = styled.div`
  width: 100%;
  margin-bottom: 35px;
	display: flex;
	justify-content: center;
`;

const SORT = {
	UPDATE_DESC: 'latest',
	UPDATE_ASC: 'oldest',
};

const List = (
	{
		openMenu,
		list = [],
		category,
		setPageNumber,
		pageNumber,
		setPageSize,
		pageSize,
		setSearchKeyword,
		searchKeyword,
		sortType,
		setSortType,
		isLoading,
		resetFilter,
		totalCount,
		onFilterChange,
	}:
	{
		openMenu: () => void;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		list: any[];
		category: ICategory;
		setPageNumber: (number) => void;
		pageNumber: number;
		setPageSize: (number) => void;
		pageSize: number;
		setSearchKeyword: (string) => void;
		searchKeyword: string;
		sortType: 'latest' | 'oldest' | '';
		setSortType: (string) => void;
		isLoading: boolean;
		resetFilter: () => void;
		totalCount: number
		onFilterChange: ({ keyword }) => void
	},
): JSX.Element => {
	const { t } = useTranslation();
	const scrollToTopContext = useContext<ScrollToTopContextType>(ScrollToTopContext) as any;
	const SORT_TYPE = [
		{ type: SORT.UPDATE_DESC, text: t('category:sort_type_update_desc') },
		{ type: SORT.UPDATE_ASC, text: t('category:sort_type_update_asc') },
	];
	return (
		<Content>
			<Header>
				<TitleContent>
					{category?.name || t('category:all_category')}
				</TitleContent>
				<ActionContent>
					<StyledDropdown
						selectedOption={(_.find(SORT_TYPE, { type: sortType }))?.text}
						options={SORT_TYPE.map(type => ({ ...type, isSelected: sortType === type.type }))}
						handleSelectOption={type => setSortType(type)}
						component={isExpand => (
							<SortContent className="sort-content" isExpand={isExpand}>
								{t('category:sort')}
								<StyledArrowDownIcon className="arrow-icon" />
							</SortContent>
						)}
					/>
					<SearchInputWrapper>
						<StyledInputBox
							type="text"
							value={searchKeyword}
							placeholder={t('category:search_placeholder')}
							onChange={event => {
								setSearchKeyword(event.target.value);
							}}
							onKeyPress={event => {
								if (event.charCode === 13 || event.keyCode === 13) {
									onFilterChange({ keyword: searchKeyword });
								}
							}}
						/>
						<StyledSearchIcon onClick={() => onFilterChange({ keyword: searchKeyword })} />
					</SearchInputWrapper>
					{/* <StyledSearchIcon /> */}
				</ActionContent>
				<ActionContentMobile onClick={openMenu}>
					<StyledFilterV2Icon />
					<ActionText>
						{t('category:filter')}
					</ActionText>
				</ActionContentMobile>
			</Header>
			{
				category?.highlights && (
					<DescContent>
						{category?.highlights}
					</DescContent>
				)
			}
			{
				list.length > 0 ? (
					<CardList list={list} isLoading={isLoading} itemNumber={3} />
				) : (
					<Empty resetFilter={resetFilter} />
				)
			}
			{
				!isLoading && totalCount > pageSize && (
					<PaginationBox>
						<Pagination
							current={pageNumber + 1}
							total={totalCount}
							defaultPageSize={pageSize}
							defaultCurrent={pageNumber + 1}
							onChange={page => {
								setPageNumber(page - 1);
								scrollToTopContext.scrollToTop();
							}}
							onShowSizeChange={(current, size) => {
								setPageSize(size);
							}}
							unit={t('pagination:rating_page_unit')}
							showSizeChanger={false}
						/>
					</PaginationBox>
				)
			}
		</Content>
	);
};

export default List;
